import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Send, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { value: "delivery_reminder", label: "Delivery reminder" },
  { value: "rating_prompt", label: "Rating prompt" },
];

export default function TestNotifications() {
  const [form, setForm] = useState({ kind: "delivery_reminder", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setBusy(true);
    setResult(null);
    try { const { data } = await api.post("/admin/notifications/test", form); setResult(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="test-notifications">
      <div className="flex items-center gap-2"><Send className="h-5 w-5 text-brand" /><h3 className="font-heading text-lg font-medium text-slate-900">Send a test message</h3></div>
      <p className="mt-1 text-sm text-slate-500">Check your Twilio and SMTP settings before going live. Sends sample order details using your saved template.</p>
      <div className="mt-4 flex gap-2">
        {KINDS.map((k) => (
          <button key={k.value} type="button" onClick={() => setForm({ ...form, kind: k.value })} data-testid={`test-kind-${k.value}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${form.kind === k.value ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{k.label}</button>
        ))}
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Phone (for text)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+447700900000" data-testid="test-phone-input" /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="test-email-input" /></div>
      </div>
      <Button onClick={send} disabled={busy || (!form.phone && !form.email)} className="mt-4 bg-brand hover:bg-brand-hover" data-testid="send-test-btn">{busy ? "Sending…" : "Send test reminder"}</Button>
      {result && (
        <div className="mt-4 space-y-2 rounded-lg bg-slate-50 p-4 text-sm" data-testid="test-result">
          {result.sms && <ResultLine label="Text" r={result.sms} testid="test-result-sms" />}
          {result.email && <ResultLine label="Email" r={result.email} testid="test-result-email" />}
          <p className="whitespace-pre-wrap border-t border-slate-200 pt-2 text-xs text-slate-500" data-testid="test-result-message">{result.message}</p>
        </div>
      )}
    </div>
  );
}

function ResultLine({ label, r, testid }) {
  return (
    <p className={`flex items-start gap-2 ${r.sent ? "text-green-700" : "text-red-600"}`} data-testid={testid}>
      {r.sent ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0" />}
      <span><b>{label}:</b> {r.sent ? "sent" : `not sent — ${r.reason || "unknown error"}`}</span>
    </p>
  );
}
